import { AfterContentInit, Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { pick, startCase, toLower } from 'lodash';

import { AuthenticationService, DateService, ErrorHandlerService, NotyService, UserService, ValidationService } from '@app/services';
import { User, ValidationKey, PrefilledFields, LocaleInput } from '@app/models';
import { TranslateService } from '@ngx-translate/core';
import { SelectOption } from '@app/shared';
import { I18nService } from '@app/core';

export type ModalResult = User | void;
export interface ModalPublicProperties {
  currentUser: User;
  ignoreEmailAndPassword?: boolean;
  ignoreCpf?: boolean;
  ignoreBirthdate?: boolean;
  forceBirthdateAs?: string;
  forceCpfAs?: string;
  forceNameAs?: string;
  prefilledFields?: PrefilledFields;
  ignoreLocale?: boolean;
  forceEdit?: boolean;
  isSigningFlow?: boolean;
  documentLocale?: LocaleInput;
}

export const userBirthdayValidations: ValidationKey[] = ['required', 'appMatchBirthdate'];
export const userCpfValidations: ValidationKey[] = ['required', 'appMatchCpf'];
export const userNameValidations: ValidationKey[] = ['required', 'appValidName', 'appFullName', 'appForbiddenNames', 'appMatchName'];

@Component({
  selector: 'app-set-missing-user-info-modal',
  templateUrl: './set-missing-user-info-modal.component.html',
  styleUrls: ['./set-missing-user-info-modal.component.scss'],
  standalone: false
})
export class SetMissingUserInfoModalComponent implements ModalPublicProperties, AfterContentInit {
  @Input() currentUser: User;
  @Input() ignoreEmailAndPassword: boolean;
  @Input() ignoreCpf: boolean;
  @Input() ignoreBirthdate: boolean;
  @Input() forceBirthdateAs: string;
  @Input() forceCpfAs: string;
  @Input() forceNameAs: string;
  @Input() prefilledFields: PrefilledFields;
  @Input() ignoreLocale: boolean;
  @Input() forceEdit = false;
  @Input() isSigningFlow = false;
  @Input() documentLocale: LocaleInput;
  form!: UntypedFormGroup;
  userForm: UntypedFormGroup;
  localeForm: UntypedFormGroup;
  isLoading = false;
  isVerifyingEmail = false;
  isFixedEmail = false;

  readonly countries: SelectOption[] = [];
  readonly languages: SelectOption[] = [];

  private normalAttributes = ['name', 'cpf', 'birthday'] as const;
  private emailAttributes = ['email', 'password'] as const;
  private localeAttributes = ['country', 'language'] as const;

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private errorHandlerService: ErrorHandlerService,
    private validationService: ValidationService,
    private notyService: NotyService,
    private authenticationService: AuthenticationService,
    private dateService: DateService,
    private i18nService: I18nService
  ) {
    this.userForm = this.formBuilder.group({});
    this.localeForm = this.formBuilder.group({});
    this.form = this.formBuilder.group({ user: this.userForm, locale: this.localeForm });
    this.countries = this.i18nService.countriesWithCodes || null;
    this.languages = this.i18nService.supportedLanguagesAsSelectOptions || null;
  }

  ngAfterContentInit() {
    if (
      this.normalAttributes.find(attr => !this.currentUser[attr]) ||
      !!this.validationService.validate(this.currentUser.birthday, userBirthdayValidations, { appMatchBirthdate: [this.forceBirthdateAs] }) ||
      !!this.validationService.validate(this.currentUser.cpf, userCpfValidations, { appMatchCpf: [this.forceCpfAs] }) ||
      !!this.validationService.validate(this.currentUser.name, userNameValidations, { appMatchName: [this.forceNameAs] }) ||
      this.forceEdit
    ) {
      this.normalAttributes.forEach(attr => {
        let value = this.currentUser[attr];
        if (attr === 'birthday' && !this.ignoreBirthdate) value = this.dateService.normalizeDate(value); // Normaliza a data para o formato correto conforme o idioma selecionado
        this.userForm.addControl(attr, this.formBuilder.control(value || ''));
      });
    }
    if (!this.ignoreEmailAndPassword && (!this.currentUser.hp_set || !this.currentUser.email_verified_at)) {
      // Como não tem como saber se o usuário tem ou não senha, assume que se não tiver email, também não tem senha, pois é uma conta criada com celular.
      const hasEmail = !!this.currentUser.email;

      this.emailAttributes.forEach(attr => {
        const value = hasEmail && attr === 'email' ? this.currentUser.email : '';
        this.userForm.addControl(attr, this.formBuilder.control(value));

        if (hasEmail && attr === 'email' && this.currentUser.email_verified_at) this.isFixedEmail = true;
      });
    }

    this.localeAttributes.forEach(attr => {
      let value = this.currentUser.locale[attr];
      if (attr === 'language') value = this.i18nService.language;
      if (attr === 'country') value = this.currentUser.locale[attr] || this.documentLocale?.country || this.i18nService.navigatorCountry(); // tenta pegar o país do browser se não tiver no user.locale e no document
      this.localeForm.addControl(attr, this.formBuilder.control(value || ''));
    });

    if (!!this.prefilledFields) this.setPrefilledFields();

    setTimeout(() => {
      this.normalAttributes.forEach(attribute => {
        if (this.currentUser[attribute] && this.userForm.get(attribute) && this.userForm.get(attribute).value) {
          this.userForm.get(attribute).markAsTouched();
        } else if (this.userForm.get(attribute)) {
          this.userForm.get(attribute).markAsUntouched();
        }
      });

      if (document.querySelector('app-set-missing-user-info-modal input')) {
        (document.querySelector('app-set-missing-user-info-modal input') as HTMLInputElement).focus();
      }

      if (this.currentUser.email && this.currentUser.name && !this.currentUser.email_verified_at && this.ignoreEmailAndPassword) {
        this.isVerifyingEmail = true;
        this.sendEmailConfirmation();
      }
    });
  }

  update() {
    let normalAttrs = pick(this.userForm.value, this.normalAttributes);
    let localeAttrs = pick(this.localeForm.value, this.localeAttributes);
    let emailAttrs = pick(this.userForm.value, this.emailAttributes);
    if (normalAttrs.birthday) normalAttrs.birthday = this.dateService.normalizeDate(normalAttrs.birthday); // Normaliza a data para o formato DD/MM/YYYY, antes de enviar para o backend
    normalAttrs = Object.keys(normalAttrs).length > 0 ? normalAttrs : null;
    emailAttrs = Object.keys(emailAttrs).length > 0 ? emailAttrs : null;

    let request$: Observable<any>;
    if (emailAttrs) {
      // se tiver email atualiza email/senha primeiro se nao atualiza só a senha por ter 'emailAttrs'
      const baseRequest$: Observable<any> = emailAttrs.email
        ? this.userService.setCurrentUserMissingInfo({ user: emailAttrs })
        : this.userService.updateUserPassword({ password: emailAttrs.password });
      request$ = baseRequest$.pipe(
        tap(() => Object.keys(emailAttrs).forEach(attr => this.userForm.removeControl(attr))),
        switchMap(() => (normalAttrs ? this.userService.updateCurrentUser({ user: normalAttrs }) : of(null))),
        switchMap(() => (localeAttrs ? this.userService.updateCurrentUserLocale({ locale: localeAttrs }) : of(null)))
      );
    } else if (normalAttrs) {
      request$ = this.userService.updateCurrentUser({ user: normalAttrs }).pipe(switchMap(() => (localeAttrs ? this.userService.updateCurrentUserLocale({ locale: localeAttrs }) : of(null))));
    } else {
      request$ = this.userService.updateCurrentUserLocale({ locale: localeAttrs });
    }

    this.isLoading = true;
    request$.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: data => {
        const email = emailAttrs?.email || this.currentUser.email;
        if (email && !this.currentUser.email_verified_at) {
          this.isVerifyingEmail = true;
          this.currentUser.email = email;
          this.sendEmailConfirmation();
        } else {
          this.modal.close(data as ModalResult);
        }
      },
      error: error => this.errorHandlerService.handleValidation(this.form, error)
    });
  }

  sendEmailConfirmation() {
    this.isLoading = true;
    this.authenticationService
      .sendEmailVerificationPrompt({ email: this.currentUser.email })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => this.notyService.success(this.translateService.instant('notyService.confirmationEmailResent')),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  changeLanguage(language: string) {
    this.i18nService.language = language;
    (this.languages as SelectOption[]) = this.i18nService.supportedLanguagesAsSelectOptions || null;
    (this.countries as SelectOption[]) = this.i18nService.countriesWithCodes || null;
    if (this.form.get('locale.language')?.value) this.form.get('locale.language').setValue(this.form.get('locale.language').value);
    if (this.form.get('locale.country')?.value) this.form.get('locale.country').setValue(this.form.get('locale.country').value);
  }

  setPrefilledFields() {
    const emailControl = this.form.get('user.email');
    const nameControl = this.form.get('user.name');
    const cpfControl = this.form.get('user.cpf');
    const birthdateControl = this.form.get('user.birthday');
    const countryControl = this.form.get('locale.country');
    const languageControl = this.form.get('locale.language');

    if (emailControl && !emailControl.value) {
      emailControl.setValue(this.prefilledFields.email);
    }

    if (nameControl && !nameControl.value) {
      nameControl.setValue(startCase(toLower(this.prefilledFields.name)));
    }

    if (cpfControl && !cpfControl.value && !this.ignoreCpf) {
      cpfControl.setValue(this.prefilledFields.cpf);
    }

    if (birthdateControl && !birthdateControl.value) {
      let birthdate = this.prefilledFields.birthdate;
      if (birthdate) {
        // Se estiver no formato yyyy-MM-dd, adaptamos para dd/MM/yyyy
        if (/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
          const [year, month, day] = birthdate.split('-');
          birthdate = `${day}/${month}/${year}`;
        }

        const normalizedBirthdate = this.dateService.normalizeDate(birthdate);
        birthdateControl.setValue(normalizedBirthdate);
      }
    }

    if (countryControl && !countryControl.value) {
      countryControl.setValue(this.prefilledFields.country);
    }

    if (languageControl && !languageControl.value) {
      languageControl.setValue(this.prefilledFields.language);
    }
  }
}
